/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

/**
 * The little coloured circle with somebody's initials in it.
 *
 * **Both functions are ported exactly from `public/app.js`, including the parts
 * that look like they could be tidied.** The colour is a recognition cue — you
 * learn that Priya is the purple one — so it has to be the *same* purple in both
 * frontends, and during the migration people will be looking at both. Two
 * plausible implementations of "hash a name to a palette index" give different
 * answers, and the rewrite's own version did:
 *
 * - the palette had different colours in a different order;
 * - it hashed with `>>> 0` and took the modulo once at the end, where vanilla
 *   takes it every iteration. Those agree only until the first overflow.
 *
 * `initials` had drifted too: a single-word name read as its first *two letters*
 * here and its first letter in vanilla, so "Priya" was PR on one board and P on
 * the other.
 */
const AVATAR_COLORS = ['#5A67D8', '#9757C7', '#D9713B', '#3F9863', '#C2860E', '#D3453B', '#2C82C9'];

export function ownerColour(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[h]!;
}

/** The first letter of up to two words: "Mo Member" → MM, "Priya" → P. */
export function initials(name: string): string {
  const letters = name
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  // A name that is only punctuation would otherwise render an empty circle.
  return letters || '?';
}
